// ─── src/components/dashboard/MeetupAttendeesTable.tsx ───────────────────────

import { useState } from "react";
import { Eye, Trash2, X, CheckCircle, XCircle, UserCheck, Undo2 } from "lucide-react";
import type { MeetupAttendee } from "../../types/meetupAttendee";

interface Props {
  rows: MeetupAttendee[];
  onDelete: (id: string) => void;
  onApproval: (id: string, accepted: boolean) => void;
  onCheckIn: (id: string) => void;
  onUndoCheckIn: (id: string) => void;
  isBusy: boolean;
}

function formatTime(iso?: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === "confirmed" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400";
  // The API still uses the internal enum "confirmed"/"waitlist"; the UI shows
  // "registered" instead of "confirmed" per product decision.
  const label = status === "confirmed" ? "registered" : status;
  return <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${cls}`}>{label}</span>;
}

function ApprovalBadge({ approvalStatus }: { approvalStatus: string }) {
  const s = approvalStatus || "pending";
  const cls =
    s === "accepted" ? "bg-emerald-500/15 text-emerald-400"
    : s === "rejected" ? "bg-red-500/15 text-red-400"
    : "bg-brand-border text-brand-muted";
  return <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${cls}`}>{s}</span>;
}

export function MeetupAttendeesTable({ rows, onDelete, onApproval, onCheckIn, onUndoCheckIn, isBusy }: Props) {
  const [selected, setSelected] = useState<MeetupAttendee | null>(null);

  if (rows.length === 0) {
    return <div className="card p-10 text-center text-brand-muted">No RSVPs for this event yet.</div>;
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border">
                <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Name</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Email</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">WhatsApp</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Governorate</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Seat</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Review</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Attended</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">RSVP&apos;d</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, idx) => (
                <tr key={a.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                  <td className="px-4 py-3 text-brand-muted">{idx + 1}</td>
                  <td className="px-4 py-3 text-white font-medium whitespace-nowrap" dir="rtl">{a.fullName}</td>
                  <td className="px-4 py-3 text-brand-muted">{a.email}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="ltr">{a.whatsapp}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="rtl">{a.governorate}</td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3"><ApprovalBadge approvalStatus={a.approvalStatus} /></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {a.checkedInAt ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs">
                        <UserCheck size={13} /> {formatTime(a.checkedInAt)}
                      </span>
                    ) : a.approvalStatus === "accepted" ? (
                      <button onClick={() => onCheckIn(a.id)} disabled={isBusy} title="Mark as attended"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40">
                        <UserCheck size={13} /> Check in
                      </button>
                    ) : (
                      <span className="text-brand-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{formatDate(a.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSelected(a)} title="View details"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
                        <Eye size={14} />
                      </button>
                      {a.approvalStatus !== "accepted" && (
                        <button onClick={() => onApproval(a.id, true)} disabled={isBusy} title="Accept attendee"
                          className="p-1.5 rounded-lg text-brand-muted hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40">
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {a.approvalStatus !== "rejected" && (
                        <button onClick={() => onApproval(a.id, false)} disabled={isBusy} title="Reject attendee (frees their seat)"
                          className="p-1.5 rounded-lg text-brand-muted hover:text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
                          <XCircle size={14} />
                        </button>
                      )}
                      {a.checkedInAt && (
                        <button onClick={() => onUndoCheckIn(a.id)} disabled={isBusy} title="Undo check-in (mis-scan)"
                          className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors disabled:opacity-40">
                          <Undo2 size={14} />
                        </button>
                      )}
                      <button onClick={() => onDelete(a.id)} disabled={isBusy} title="Remove (auto-promotes next waitlisted)"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <DetailModal row={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function Field({ label, value, rtl }: { label: string; value?: string | null; rtl?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>{value}</p>
    </div>
  );
}

function DetailModal({ row, onClose }: { row: MeetupAttendee; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg">{row.fullName}</p>
            <p className="text-brand-muted text-sm" dir="ltr">{row.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={row.status} />
            <ApprovalBadge approvalStatus={row.approvalStatus} />
            <button onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Gender" value={row.gender} rtl />
            <Field label="Age group" value={row.ageGroup} rtl />
            <Field label="WhatsApp" value={row.whatsapp} />
            <Field label="RSVP'd" value={formatDate(row.createdAt)} />
          </div>

          <div className="pt-4 border-t border-brand-border grid grid-cols-2 gap-4">
            <Field label="Governorate" value={row.governorate} rtl />
            <Field label="City / area" value={row.cityArea} rtl />
            <Field label="Current status" value={row.currentStatus} rtl />
            <Field label="LinkedIn" value={row.linkedInUrl} />
          </div>

          <div className="pt-4 border-t border-brand-border">
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-2">Skills / Background</p>
            <div className="flex flex-wrap gap-2" dir="rtl">
              {row.skills.map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-lg text-xs bg-brand-teal/15 text-brand-tealLight">{s}</span>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-brand-border space-y-4">
            <Field label="Why they want to attend" value={row.whyAttend} rtl />
            <Field label="Questions for the event" value={row.questionsForEvent} rtl />
          </div>
        </div>
      </div>
    </div>
  );
}
